// Export utility functions for shared export functionality

// Import tier color functions from centralized service
import { getTierColors, getTierColor } from '../../../services/TierColorService';
// Import revenue calculation functions from centralized service
import { 
  calculateTotalRevenue as calcTotalRevenue, 
  calculateFilteredRevenue as calcFilteredRevenue,
  parseCurrencyToNumber,
  formatCurrency
} from '../../../services/RevenueCalculationService';

// Re-export tier color functions for backward compatibility
export { getTierColors, getTierColor };

// Re-export revenue calculation functions for backward compatibility
export const calculateTotalRevenue = calcTotalRevenue;
export const calculateFilteredRevenue = (sortedClients) => {
  const result = calcFilteredRevenue(sortedClients);
  return result.total; // Return just the total for backward compatibility
};

// Get client details for export formatting
export const getClientDetails = (client) => {
  // Helper function to safely get nested properties
  const safeGet = (obj, path, defaultValue = 'N/A') => {
    try {
      return path.split('.').reduce((current, key) => current?.[key], obj) || defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // Use centralized currency formatting
  const formatCurrencyLocal = (value) => {
    return formatCurrency(value);
  };

  // Helper function to format date
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === '') return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr; // Return original if not a valid date
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr; // Return original if parsing fails
    }
  };

  // Extract and format client details
  const details = {
    revenue: formatCurrency(safeGet(client, 'Total Revenue')),
    lastBookingRevenue: formatCurrency(safeGet(client, 'Last Booking Revenue')),
    lastBooked: formatDate(safeGet(client, 'Last Booked')),
    tier: safeGet(client, 'Tier', 'Unknown'),
    timeZone: safeGet(client, 'Time Zone'),
    contactName: safeGet(client, 'Key Contact'),
    contactEmail: safeGet(client, 'Email'),
    segment: safeGet(client, 'Segment'),
    rn: safeGet(client, 'RN'),
    previousManager: safeGet(client, 'Previously Assigned')
  };

  return details;
};

// Check if a client is from external manager (manually added)
export const isExternalClient = (client, portfolioType, activeManager) => {
  return portfolioType === 'manager' && activeManager && client.conference_manager !== activeManager;
};

// Format export filename based on portfolio type and date
export const formatExportFilename = (portfolioType, activeManager, fileExtension = 'xls') => {
  const today = new Date();
  const dateStr = today.toISOString().split('T')[0];
  const portfolioName = portfolioType === 'manager' && activeManager ? `${activeManager}_Portfolio` : 'Custom_Portfolio';
  return `${portfolioName}_${dateStr}.${fileExtension}`;
};

// Generate export metadata
export const generateExportMetadata = (pinnedClients, portfolioType, activeManager) => {
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  
  return {
    title: portfolioType === 'manager' && activeManager ? `${activeManager}: Client Portfolio` : 'Custom Portfolio',
    subtitle: `Generated on ${formattedDate} | Total Clients: ${pinnedClients.length} | Total Revenue: ${calculateTotalRevenue(pinnedClients)}`,
    date: formattedDate,
    clientCount: pinnedClients.length,
    totalRevenue: calculateTotalRevenue(pinnedClients),
    portfolioType,
    activeManager
  };
};

// Escape HTML special characters for safe export
export const escapeHtml = (text) => {
  if (text === null || text === undefined) return '';
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
};

// Generate export table headers
export const getExportTableHeaders = () => {
  return [
    'Client Name',
    'Group Size', 
    'Country',
    'Manager',
    'Total Revenue',
    'Time Zone',
    'Tier',
    'Last Booking Revenue',
    'Last Booked',
    'Key Contact',
    'Email',
    'Segment',
    'RN'
  ];
};

// Extract export row data from client
export const extractExportRowData = (client, getClientDetails) => {
  const details = getClientDetails(client);
  
  return [
    client.client_name,
    client.group_size,
    client.country,
    client.conference_manager,
    details.revenue,
    details.timeZone,
    details.tier,
    details.lastBookingRevenue,
    details.lastBooked,
    details.contactName,
    details.contactEmail,
    details.segment,
    details.rn
  ];
};

// Default export object with all utilities
const ExportUtils = {
  calculateTotalRevenue,
  calculateFilteredRevenue,
  getClientDetails,
  getTierColors,
  getTierColor,
  isExternalClient,
  formatExportFilename,
  generateExportMetadata,
  escapeHtml,
  getExportTableHeaders,
  extractExportRowData
};

export default ExportUtils;
